import {
  OfferCategory,
  OffersQuery,
  useOffersQuery,
} from "@common/utils/graphql";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./css/slick-custom.css";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryResult } from "@apollo/client";
import { useMemo } from "react";
import { DashboardTile } from "../components/DashboardTile";
import { Body } from "@hypercard/web-components";
import { Check, Percent } from "@phosphor-icons/react";
import { IconBox } from "@common/components";
import { twMerge } from "@/utils/utils";

type Props = {
  className?: string;
};

export const OffersTile = ({ className }: Props) => {
  const { data: offerList, loading: offerListLoading } = useOffersQuery({
    variables: {
      categories: [OfferCategory.KFactor, OfferCategory.Other],
    },
  }) as QueryResult<OffersQuery>;

  const offers = useMemo(() => {
    if (offerList?.offers?.__typename !== "QueryOffersSuccess") {
      return [];
    }
    return offerList?.offers?.data.offers;
  }, [offerList]);

  const sliderSettings = {
    dots: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: false,
  };

  return (
    <DashboardTile
      className={twMerge(
        "flex h-full min-h-[250px] flex-col justify-between overflow-hidden p-0",
        className,
      )}
    >
      <div className="relative h-full w-full bg-bubble-gradient">
        {offerListLoading ? (
          <div className="h-full w-full p-6">
            <Skeleton className="h-full w-full rounded-sm" />
          </div>
        ) : offers.length > 0 ? (
          <Slider {...sliderSettings} className="h-[calc(100%-32px)]">
            {offers.map((offer, i) => (
              <div className="h-full w-full" key={`offer-${i}`}>
                <div className="flex h-full flex-col items-center justify-center px-8 pt-8 text-center">
                  <div className="relative flex justify-center">
                    {offer?.iconImageUrl ? (
                      <>
                        {offer.isUsageLimitReached && (
                          <Check className="absolute right-0 top-0 size-4 -translate-y-1 translate-x-1" />
                        )}
                        <img
                          className="mb-8 h-[52px]"
                          src={offer.iconImageUrl}
                        />
                      </>
                    ) : offer.isUsageLimitReached ? (
                      <Check name="check" className="mb-8 size-8" />
                    ) : null}
                  </div>
                  <Body size="lg" weight="medium">
                    {offer.title}
                  </Body>
                  <Body
                    size="sm"
                    weight="regular"
                    className="mt-2 text-white/50"
                  >
                    {offer.details}
                  </Body>
                </div>
              </div>
            ))}
          </Slider>
        ) : (
          <div className="flex h-full flex-col items-center justify-center">
            <IconBox>
              <Percent className="size-8" />
            </IconBox>
            <Body size="lg" weight="medium" className="mt-6">
              Get ready for Hypercard!
            </Body>
            <Body
              size="sm"
              weight="regular"
              className="mt-2 whitespace-pre text-center text-white/70"
            >
              {"Sign up to be notified and access incredible offers."}
            </Body>
          </div>
        )}
      </div>
    </DashboardTile>
  );
};
